import { DEFAULT_LOCALE, LOCALES, type Locale } from '@brig/api-types';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { en } from './locales/en.ts';
import { ru } from './locales/ru.ts';
import { uk } from './locales/uk.ts';

export const resources = {
  en: { translation: en },
  uk: { translation: uk },
  ru: { translation: ru },
} as const;

/** Language names written in their own language, for the switcher. */
export const ENDONYMS: Record<Locale, string> = {
  en: 'English',
  uk: 'Українська',
  ru: 'Русский',
};

export const LANGUAGE_STORAGE_KEY = 'brig.lang';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** The current UI language, narrowed to a supported locale. */
export function currentLocale(): Locale {
  const lang = i18n.resolvedLanguage ?? i18n.language;
  return isLocale(lang) ? lang : DEFAULT_LOCALE;
}

function syncDocumentLanguage(lng: string) {
  if (typeof document !== 'undefined') document.documentElement.lang = isLocale(lng) ? lng : DEFAULT_LOCALE;
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: [...LOCALES],
    fallbackLng: DEFAULT_LOCALE,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    returnNull: false,
  });

i18n.on('languageChanged', syncDocumentLanguage);
syncDocumentLanguage(i18n.resolvedLanguage ?? DEFAULT_LOCALE);

export default i18n;
